/**
 * @file RoundChip.tsx
 * Restyling of Chip component to match designs.
 */
import React, { ComponentType } from "react";

import { Chip, ChipProps, IconProps } from "@mui/material";

import {
  requestStatusLabelColors,
  priceSummaryStatusLabelColors,
} from "../../design-system/colors";

type TextTransform =
  | "-moz-initial"
  | "inherit"
  | "initial"
  | "revert"
  | "unset"
  | "none"
  | "full-width"
  | "capitalize"
  | "full-size-kana"
  | "lowercase"
  | "uppercase";

type RequestStatusVariants = "NEW_QUOTE" | "DRAFT" | "EMAIL_SENT";
type PriceSummaryStatusVariants = "PAID" | "NOT_PAID" | "PARTIAL";

type RoundChipProps = {
  variant?: RequestStatusVariants | PriceSummaryStatusVariants;
  label?: string;
  icon?: ComponentType<IconProps>;
  color?: string;
  backgroundColor?: string;
  textTransform?: TextTransform;
  iconProps?: IconProps;
  inputProps?: ChipProps;
};

function RoundChip(props: RoundChipProps) {
  const {
    label,
    icon,
    color,
    backgroundColor,
    iconProps,
    inputProps,
    variant,
    textTransform,
  } = props;

  const Icon = icon;

  const requestStatusMap = {
    NEW_QUOTE: "NEW",
    DRAFT: "DRAFT",
    EMAIL_SENT: "SENT",
  };

  const styles = {
    root: {
      borderRadius: "50px",
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: 12,
      ...(color && { color }),
      ...(backgroundColor && { backgroundColor }),

      "& span": {
        ...(textTransform && { textTransform }),
      },
    },
  };

  if (requestStatusMap.hasOwnProperty(variant)) {
    return (
      <RoundChip
        label={requestStatusMap[variant]}
        {...requestStatusLabelColors[variant]}
        inputProps={inputProps}
      />
    );
  }

  const priceSummaryStatusMap = {
    PAID: "PAID",
    NOT_PAID: "NOT PAID",
    PARTIAL: "PARTIAL",
  };

  if (priceSummaryStatusMap.hasOwnProperty(variant)) {
    return (
      <RoundChip
        label={priceSummaryStatusMap[variant]}
        {...priceSummaryStatusLabelColors[variant]}
        inputProps={inputProps}
      />
    );
  }

  return (
    <Chip
      sx={styles.root}
      size="small"
      label={label}
      icon={icon && <Icon fontSize="small" {...iconProps} style={{ color }} />}
      {...inputProps}
    />
  );
}

export default RoundChip;
